import axios, { CancelTokenSource } from 'axios';
import { useCallback, useRef, useState } from 'react';
import { api } from 'src/constants/apiRoutes';
import useAsync from 'src/hooks/useAsync';
import axiosInstance from 'src/utils/axios';
import { IAllSuppliers } from './useGetOnlineAndOfflineSuppliers';

export interface ReviewPoRequest {
  attachmentId: number;
  companyId: string;
  poNumber?: string;
  poId?: number;
}

const PoMatchStatusArray = [
  'PoNumberNotFound',
  'NoMatch',
  'ConfirmedMatchCreatedByCurrentUser',
  'ConfirmedMatchCreatedByOtherUser',
  'InvalidMatch',
  'MultipleMatch',
] as const;

interface supplierResponse {
  customerIdMatchedSuppliers: IAllSuppliers[];
  addressMatchedSupplier: IAllSuppliers;
  matchConfidence: string | null;
  ocrSupplierName: string | null;
}

export type PoMatchStatus = typeof PoMatchStatusArray[number];

export interface ReviewPoResponse {
  matchStatus: PoMatchStatus;
  isPoRequiredToCreateInvoice: boolean;
  canReceiveInvoices: boolean;
  canReceiveInvoicesCompany: boolean;
  poMatchedSuppliers: IAllSuppliers[];
  matchConfidence: string | null;
  ocrPoNumber: string | null;
  poId: string | null;
  supplierMatchReviewResult: supplierResponse;
}

const useReviewPoApi = () => {
  const reqRef = useRef<CancelTokenSource>();
  const [initialReviewPoValidation, setInitialReviewPoValidation] = useState(
    {} as ReviewPoResponse
  );
  const call = useCallback(async (reqBody?: ReviewPoRequest) => {
    try {
      if (!reqBody) return {} as ReviewPoResponse;
      const req = axios.CancelToken.source();
      if (reqRef.current) {
        reqRef.current.cancel();
      }
      reqRef.current = req;

      const res = await axiosInstance.post<ReviewPoResponse>(api.reviewPo(), reqBody, {
        cancelToken: req.token,
      });
      return res.data;
    } catch (error) {
      throw error;
    }
  }, []);

  const { execute, isLoading, value, setValue, error } = useAsync<
    ReviewPoResponse,
    ReviewPoRequest
  >(call, false, undefined, {} as ReviewPoResponse);

  return {
    reviewPoMethod: execute,
    isReviewPoLoading: isLoading,
    reqRef,
    reviewPoValidations: value,
    setReviewPoValidations: setValue,
    initialReviewPoValidation,
    setInitialReviewPoValidation,
    reviewPoError: error,
  };
};

export default useReviewPoApi;
